import { useState,useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { ArrowLeft, Check, ShoppingCart, Sparkles, Ticket } from "lucide-react";
import {
  MarketplaceApp,
  InstalledAppData,
  useMarketAppSettingsData,
} from "@/lib/settings/marketplaceData";
import {
  consultationBundles,
  ConsultationBundle,
} from "@/lib/settings/consultationBundlesData";
import { addPromotionalCredit,getCompanyPurchaseHistory } from "@/dispatchers/companyServiceBillingDispatchers";
import {
  showError,
  showSuccess,
} from "@/components/ToastedMessage/ToastedMessage";
import useTitanApp from "@/hooks/useTitanApp";

interface ConsultationBundlesAppDetailsProps {
  app: MarketplaceApp;
  installedAppData?: InstalledAppData;
  isDarkMode?: boolean;
  onBack: () => void;
  onInstall: (
    appId: string,
    selectedLocationIds: string[],
    apiKeys: Record<string, any>,
  ) => void;
}

const formatGbp = (value: number) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

const promotionTypeMap: Record<string, number> = {
  launcher: 5,
  explorer: 6,
  voyager: 7,
  conqueror: 8,
};

export function ConsultationBundlesAppDetails({
  app,
  installedAppData,
  isDarkMode,
  onBack,
  onInstall,
}: ConsultationBundlesAppDetailsProps) {
  
 const { companyServiceBillingState } = useTitanApp();
 
    const {
    isCompanyLedgerPurchaseHistoryLoading
  } = companyServiceBillingState || {};

  const { companyId } = useMarketAppSettingsData();
  const [selectedBundle, setSelectedBundle] =
    useState<ConsultationBundle | null>(null);
  const [confirmingPurchase, setConfirmingPurchase] = useState(false);
  const [isPurchasing, setIsPurchasing] = useState(false);
const [purchaseHistory, setPurchaseHistory] = useState([]);
const [purchaseSummary, setPurchaseSummary] = useState({
  count: 0,
  totalSpent: 0,
});


const totalPurchased = purchaseHistory.reduce(
  (sum, item) => sum + (item.prePurchasedConsultation || 0),
  0,
);

const totalSpent = purchaseSummary.totalSpent;

  const handlePurchase = (bundle: ConsultationBundle) => {
    setSelectedBundle(bundle);
    setConfirmingPurchase(true);
  };


const loadPurchaseHistory = async () => {
  if (!companyId) return;

  try {

    const result = await getCompanyPurchaseHistory(companyId);

    setPurchaseHistory(result?.details || []);

    setPurchaseSummary({
      count: result?.count || 0,
      totalSpent: result?.totalSpent || 0,
    });
  } catch (error) {
    console.error(error);

    setPurchaseHistory([]);

    setPurchaseSummary({
      count: 0,
      totalSpent: 0,
    });
  } finally {
  }
};

  const confirmPurchase = async () => {
    if (!selectedBundle || !companyId) {
      showError("Company details are missing. Please try again.");
      return;
    }

    setIsPurchasing(true);

    const purchaseCost = selectedBundle.discountPrice.toFixed(2);
    const promotionTitle = promotionTypeMap[selectedBundle.id];

    if (!promotionTitle) {
      setIsPurchasing(false);
      showError("This bundle does not have a valid promotion type.");
      return;
    }

    const promotionalCreditPayload = {
      companyID: companyId,
      ODS: null,
      LedgerItemType: 5,
      Debit: "0.00",
      Credit: "0",
      LedgerItemCost: purchaseCost,
      NetLedgerItemCost: purchaseCost,
      UserPackDetail: null,
      ConsultationDetail: null,
      SmsContent: null,
      PromotionalCreditsDetails: {
        PromotionTitle: promotionTitle,
        PromotionNotes: `${selectedBundle.name} bundle purchase`,
        CreditAmount: selectedBundle.discountPrice,
        PrePurchasedConsultation: selectedBundle.consults,
      },
      IsBilled: false,
    };

    const apiResult = await addPromotionalCredit(promotionalCreditPayload);

    if (!apiResult) {
      setIsPurchasing(false);
      showError("Bundle purchase could not be completed.");
      return;
    }

    showSuccess(`${selectedBundle.name} bundle purchased successfully.`);
    await loadPurchaseHistory();
    setIsPurchasing(false);
    setConfirmingPurchase(false);
    setSelectedBundle(null);
  };
  
  useEffect(() => {
  loadPurchaseHistory();
}, [companyId]);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className={
            isDarkMode ? "text-gray-300 hover:text-white hover:bg-gray-700" : ""
          }
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Marketplace
        </Button>
      </div>

      {/* Title */}
      <div className="flex items-start gap-4">
        <div
          className={`flex items-center justify-center w-16 h-16 rounded-lg text-3xl flex-shrink-0 ${
            isDarkMode ? "bg-gray-800" : "bg-gray-100"
          }`}
        >
          {app.imageEmoji || "🎟️"}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <h2
              className={`text-2xl font-bold ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              {app.name}
            </h2>
            <Badge variant="outline" className="text-xs">
              {app.category}
            </Badge>
          </div>
          <p className={isDarkMode ? "text-gray-300" : "text-gray-600"}>
            {app.description}
          </p>
        </div>
      </div>

      {/* Purchase History Summary */}
      {purchaseHistory.length > 0 && (
        <div
          className={`rounded-lg border p-4 ${
            isDarkMode
              ? "border-purple-800 bg-purple-900/20"
              : "border-purple-200 bg-purple-50"
          }`}
        >
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Ticket
                className={`w-5 h-5 ${
                  isDarkMode ? "text-purple-300" : "text-purple-700"
                }`}
              />
              <div>
                <p
                  className={`text-sm font-semibold ${
                    isDarkMode ? "text-purple-200" : "text-purple-900"
                  }`}
                >
                  Bundle credits purchased
                </p>
                <p
                  className={`text-xs ${
                    isDarkMode ? "text-purple-300" : "text-purple-700"
                  }`}
                >
                  Auto-applied to your monthly consultation fees
                </p>
              </div>
            </div>
            <div className="flex gap-6 text-right">
              <div>
                <div
                  className={`text-2xl font-bold ${
                    isDarkMode ? "text-purple-200" : "text-purple-900"
                  }`}
                >
                  {totalPurchased.toLocaleString()}
                </div>
                <div
                  className={`text-xs ${
                    isDarkMode ? "text-purple-300" : "text-purple-700"
                  }`}
                >
                  Consultations
                </div>
              </div>
              <div>
                <div
                  className={`text-2xl font-bold ${
                    isDarkMode ? "text-purple-200" : "text-purple-900"
                  }`}
                >
                  {formatGbp(totalSpent)}
                </div>
                <div
                  className={`text-xs ${
                    isDarkMode ? "text-purple-300" : "text-purple-700"
                  }`}
                >
                  Total spent
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bundle Cards */}
      <div>
        <h3
          className={`text-lg font-semibold mb-4 ${
            isDarkMode ? "text-white" : "text-gray-900"
          }`}
        >
          Choose a bundle to purchase
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {consultationBundles.map((bundle) => {
            const isPopular = bundle.popular;
            return (
              <div
                key={bundle.id}
                className={`rounded-lg border p-5 flex flex-col ${
                  isPopular
                    ? isDarkMode
                      ? "border-purple-500 bg-purple-900/20"
                      : "border-purple-400 bg-purple-50"
                    : isDarkMode
                      ? "border-gray-700 bg-gray-800"
                      : "border-gray-200 bg-white"
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4
                      className={`text-lg font-bold ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {bundle.name}
                    </h4>
                    {bundle.description && (
                      <p
                        className={`text-xs mt-1 ${
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        {bundle.description}
                      </p>
                    )}
                  </div>
                  {isPopular && (
                    <Badge className="bg-purple-600 hover:bg-purple-600 text-white">
                      <Sparkles className="w-3 h-3 mr-1" />
                      Popular
                    </Badge>
                  )}
                </div>

                <div className="mb-3">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span
                      className={`text-3xl font-bold ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {formatGbp(bundle.discountPrice)}
                    </span>
                    <span
                      className={`text-base line-through ${
                        isDarkMode ? "text-gray-500" : "text-gray-400"
                      }`}
                    >
                      {formatGbp(bundle.ticketPrice)}
                    </span>
                    <Badge className="bg-green-600 hover:bg-green-600 text-white">
                      {`-${bundle.discountPercent}%`}
                    </Badge>
                  </div>
                  <div
                    className={`text-xs mt-1 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    {`Bundle amount · save ${formatGbp(
                      bundle.ticketPrice - bundle.discountPrice,
                    )}`}
                  </div>
                </div>

                <ul className="space-y-1.5 mb-5 text-sm flex-1">
                  <li
                    className={`flex items-center gap-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    <Check className="w-4 h-4 text-green-500" />
                    {bundle.consults.toLocaleString()} consultation
                    {bundle.consults !== 1 ? "s" : ""}
                  </li>
                  <li
                    className={`flex items-center gap-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    <Check className="w-4 h-4 text-green-500" />
                    {formatGbp(bundle.consultPrice)} per consult
                  </li>
                  <li
                    className={`flex items-center gap-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    <Check className="w-4 h-4 text-green-500" />
                    {`${bundle.discountPercent}% saving vs. pay-as-you-go`}
                  </li>
                </ul>

                <Button
                  onClick={() => handlePurchase(bundle)}
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                >
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Purchase for {formatGbp(bundle.discountPrice)}
                </Button>
              </div>
            );
          })}
        </div>
      </div>

   
<div
  className={`border rounded-lg overflow-hidden ${
    isDarkMode ? "border-gray-700" : "border-gray-200"
  }`}
>
  <div
    className={`px-4 py-3 border-b ${
      isDarkMode
        ? "border-gray-700 bg-gray-800"
        : "border-gray-200 bg-gray-50"
    }`}
  >
    <h3
      className={`font-semibold ${
        isDarkMode ? "text-white" : "text-gray-900"
      }`}
    >
      Purchase History
    </h3>
  </div>

  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr
          className={`text-left ${
            isDarkMode
              ? "bg-gray-800 text-gray-300"
              : "bg-gray-50 text-gray-700"
          }`}
        >
          <th className="px-4 py-2 font-medium">Bundle</th>
          <th className="px-4 py-2 font-medium text-right">Consults</th>
          <th className="px-4 py-2 font-medium text-right">Amount</th>
          <th className="px-4 py-2 font-medium text-right">Date</th>
        </tr>
      </thead>

      <tbody
        className={
          isDarkMode
            ? "divide-y divide-gray-700"
            : "divide-y divide-gray-200"
        }
      >
        {isCompanyLedgerPurchaseHistoryLoading ? (
          [...Array(5)].map((_, idx) => (
            <tr key={idx}>
              <td className="px-4 py-3">
                <div
                  className={`h-4 w-32 rounded animate-pulse ${
                    isDarkMode ? "bg-gray-700" : "bg-gray-200"
                  }`}
                />
              </td>

              <td className="px-4 py-3 text-right">
                <div
                  className={`h-4 w-16 ml-auto rounded animate-pulse ${
                    isDarkMode ? "bg-gray-700" : "bg-gray-200"
                  }`}
                />
              </td>

              <td className="px-4 py-3 text-right">
                <div
                  className={`h-4 w-20 ml-auto rounded animate-pulse ${
                    isDarkMode ? "bg-gray-700" : "bg-gray-200"
                  }`}
                />
              </td>

              <td className="px-4 py-3 text-right">
                <div
                  className={`h-4 w-24 ml-auto rounded animate-pulse ${
                    isDarkMode ? "bg-gray-700" : "bg-gray-200"
                  }`}
                />
              </td>
            </tr>
          ))
        ) : purchaseHistory.length > 0 ? (
          [...purchaseHistory].reverse().map((p, idx) => (
            <tr
              key={`${p.createdAt}-${idx}`}
              className={isDarkMode ? "text-gray-200" : "text-gray-800"}
            >
              <td className="px-4 py-2 font-medium">
                {p.promotionNotes}
              </td>
              <td className="px-4 py-2 text-right">
                {p.prePurchasedConsultation?.toLocaleString()}
              </td>
              <td className="px-4 py-2 text-right">
                {formatGbp(p.creditAmount)}
              </td>
              <td className="px-4 py-2 text-right">
                {new Date(p.createdAt).toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
              </td>
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan={4} className="text-center py-6 text-gray-500">
              No purchase history found
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
</div>

      {/* Confirm Purchase Dialog */}
      <Dialog
        open={confirmingPurchase}
        onOpenChange={(open) => {
          setConfirmingPurchase(open);
          if (!open) setSelectedBundle(null);
        }}
      >
        <DialogContent
          className={isDarkMode ? "bg-gray-900 border-gray-700" : ""}
        >
          <DialogHeader>
            <DialogTitle
              className={isDarkMode ? "text-white" : "text-gray-900"}
            >
              Confirm Bundle Purchase
            </DialogTitle>
            <DialogDescription
              className={isDarkMode ? "text-gray-400" : "text-gray-600"}
            >
              You are about to purchase the {selectedBundle?.name} bundle. The
              bundle amount will be charged to your account immediately.
            </DialogDescription>
          </DialogHeader>

          {selectedBundle && (
            <div
              className={`rounded-lg border p-4 space-y-2 ${
                isDarkMode
                  ? "border-gray-700 bg-gray-800"
                  : "border-gray-200 bg-gray-50"
              }`}
            >
              <div className="flex justify-between">
                <span
                  className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                >
                  Bundle
                </span>
                <span
                  className={`font-medium ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  {selectedBundle.name}
                </span>
              </div>
              <div className="flex justify-between">
                <span
                  className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                >
                  Consultations
                </span>
                <span className={isDarkMode ? "text-white" : "text-gray-900"}>
                  {selectedBundle.consults.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span
                  className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                >
                  Effective price per consult
                </span>
                <span className={isDarkMode ? "text-white" : "text-gray-900"}>
                  {formatGbp(selectedBundle.consultPrice)}
                </span>
              </div>
              <div className="flex justify-between">
                <span
                  className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                >
                  Original value
                </span>
                <span
                  className={`line-through ${
                    isDarkMode ? "text-gray-500" : "text-gray-400"
                  }`}
                >
                  {formatGbp(selectedBundle.ticketPrice)}
                </span>
              </div>
              <div className="flex justify-between">
                <span
                  className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                >
                  Discount
                </span>
                <span className="text-green-600 dark:text-green-400 font-medium">
                  {`-${selectedBundle.discountPercent}% (${formatGbp(
                    selectedBundle.ticketPrice - selectedBundle.discountPrice,
                  )})`}
                </span>
              </div>
              <div className="flex justify-between pt-2 border-t border-gray-300 dark:border-gray-700">
                <span
                  className={`font-semibold ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Bundle amount
                </span>
                <span
                  className={`text-xl font-bold ${
                    isDarkMode ? "text-purple-400" : "text-purple-600"
                  }`}
                >
                  {formatGbp(selectedBundle.discountPrice)}
                </span>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              disabled={isPurchasing}
              onClick={() => {
                setConfirmingPurchase(false);
                setSelectedBundle(null);
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={isPurchasing}
              onClick={confirmPurchase}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              {isPurchasing ? "Processing..." : "Confirm Purchase"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
